import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import AuthShell from '../components/layout/AuthShell';
import AlertMessage from '../components/ui/AlertMessage';
import PasswordInput from '../components/ui/PasswordInput';
import PasswordRules from '../components/ui/PasswordRules';

export default function RegisterPage() {
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '', inviteCode: '' });
  const [tosAccepted, setTosAccepted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inviteRequired, setInviteRequired] = useState(false);
  const [registered, setRegistered] = useState(null);   // { email, verificationSent }
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  const [fixingEmail, setFixingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [fixState, setFixState] = useState('idle');    // idle | saving
  const [fixError, setFixError] = useState(null);

  useEffect(() => {
    api.get('/invite/status')
      .then(res => setInviteRequired(res.data.inviteSystem === 'on'))
      .catch(() => setInviteRequired(false));
  }, []);

  const handleResend = async () => {
    setResendState('sending');
    try {
      await api.post('/auth/resend-verification', { email: registered.email });
    } catch {
      /* a válasz szándékosan mindig azonos */
    }
    setResendState('sent');
  };

  // Elgépelt cím javítása: a fiók még megerősítetlen, a jelszót ismerjük az
  // imént kitöltött űrlapból, így elég az új címet bekérni.
  const handleFixEmail = async (e) => {
    e.preventDefault();
    setFixError(null);
    setFixState('saving');
    try {
      const res = await api.post('/auth/change-email', {
        username: form.username,
        password: form.password,
        email: newEmail,
      });
      setRegistered({ email: newEmail, verificationSent: res.data.verificationSent !== false });
      setFixingEmail(false);
      setResendState('idle');
      setNewEmail('');
    } catch (err) {
      setFixError(err.response?.data?.error || 'Could not update the email address.');
    } finally {
      setFixState('idle');
    }
  };

  const validate = () => {
    if (form.password !== form.confirm) return 'Passwords do not match.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(form.password)) return 'Password must contain at least one uppercase letter.';
    if (!/[0-9]/.test(form.password)) return 'Password must contain at least one number.';
    if (inviteRequired && !form.inviteCode.trim()) return 'An invite code is required to register.';
    if (!tosAccepted) return 'You must accept the Terms of Service to register.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validErr = validate();
    if (validErr) { setError(validErr); return; }
    setError(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/register', {
        username: form.username,
        email: form.email,
        password: form.password,
        inviteCode: form.inviteCode.trim().toUpperCase() || undefined,
        tosAccepted,
      });
      setRegistered({ email: form.email, verificationSent: res.data.verificationSent !== false });
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred during registration.');
    } finally {
      setLoading(false);
    }
  };

  const confirmMismatch = form.confirm.length > 0 && form.confirm !== form.password;

  // Sikeres regisztráció után nem a login jön, hanem a "nézd meg a postaládád"
  // képernyő — belépni csak megerősített email címmel lehet.
  if (registered) {
    return (
      <AuthShell title="Check your email" subtitle="One last step before you can sign in">
        {/* Boríték-ikon, hogy a képernyő ne csak szöveg legyen */}
        <div className="flex justify-center mb-6">
          <span className="w-14 h-14 rounded-2xl bg-brand-500/12 border border-brand-400/25
                           text-brand-200 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                 strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
          </span>
        </div>

        <AlertMessage
          msg={registered.verificationSent
            ? {
                type: 'success',
                text: `We sent a verification link to ${registered.email}. Open it to activate your account — the link is valid for 24 hours.`,
              }
            : {
                type: 'error',
                text: 'Your account was created, but we could not send the verification email. Try requesting a new one below.',
              }}
        />

        <p className="text-sm text-gray-500 mb-5 leading-relaxed">
          Didn't get it? Check your spam folder first, then request a new link.
        </p>

        {fixingEmail ? (
          <form onSubmit={handleFixEmail} className="surface-raised p-4 mb-3 space-y-3">
            <p className="text-sm text-gray-400 leading-relaxed">
              Enter the correct address — we'll move the account to it and send a new link.
            </p>
            <AlertMessage msg={fixError ? { type: 'error', text: fixError } : null} className="mb-0" />
            <input
              type="email"
              className="input"
              placeholder="email@example.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <div className="flex gap-2">
              <button type="submit" className="btn-primary flex-1 py-2.5" disabled={fixState === 'saving'}>
                {fixState === 'saving' ? 'Saving…' : 'Update email'}
              </button>
              <button type="button" className="btn-secondary py-2.5 px-4"
                onClick={() => { setFixingEmail(false); setFixError(null); }}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-2.5 mb-3">
            <button
              type="button"
              className="btn-secondary w-full py-3"
              onClick={handleResend}
              disabled={resendState !== 'idle'}
            >
              {resendState === 'idle' && 'Resend verification email'}
              {resendState === 'sending' && 'Sending…'}
              {resendState === 'sent' && 'New link sent'}
            </button>

            <button
              type="button"
              className="btn-ghost w-full py-2.5 text-sm"
              onClick={() => setFixingEmail(true)}
            >
              Wrong email address? Change it
            </button>
          </div>
        )}

        <Link to="/login" className="btn-primary w-full py-3 mt-2">
          Go to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create account"
      subtitle="Join MortisEngine today"
      footer={
        <p className="text-sm text-gray-500">
          Already have an account? <Link to="/login" className="link">Sign in</Link>
        </p>
      }
    >
      {inviteRequired && (
        <div className="flex items-start gap-2.5 text-sm px-4 py-3 rounded-lg mb-6
                        bg-brand-500/10 border border-brand-400/25 text-brand-200">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
               strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 mt-0.5">
            <rect x="4" y="10" width="16" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 018 0v3" />
          </svg>
          <span className="leading-relaxed">
            Registration is invite-only. You need a valid invite code to register.
          </span>
        </div>
      )}

      <AlertMessage msg={error ? { type: 'error', text: error } : null} />

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="label">Username</label>
          <input type="text" className="input" placeholder="e.g. darkuser99"
            value={form.username}
            onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
            autoComplete="username" required />
          <p className="text-xs text-gray-600 mt-2">3-20 characters, letters, numbers and _ only</p>
        </div>

        <div>
          <label className="label">Email</label>
          <input type="email" className="input" placeholder="email@example.com"
            value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            autoComplete="email" required />
        </div>

        <div>
          <label className="label">Password</label>
          <PasswordInput
            value={form.password}
            onChange={v => setForm(p => ({ ...p, password: v }))}
            autoComplete="new-password"
            required
          />
          <PasswordRules value={form.password} />
        </div>

        <div>
          <label className="label">Confirm Password</label>
          <PasswordInput
            value={form.confirm}
            onChange={v => setForm(p => ({ ...p, confirm: v }))}
            autoComplete="new-password"
            required
            className={confirmMismatch ? 'border-red-500/50' : ''}
          />
          {confirmMismatch && (
            <p className="text-xs text-red-400 mt-2">Passwords do not match.</p>
          )}
        </div>

        {/* Invite code mező — mindig látszik ha be van kapcsolva, egyébként rejtett */}
        {inviteRequired && (
          <div>
            <label className="label">
              Invite Code <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="input font-mono tracking-widest uppercase"
              placeholder="XXXX-XXXX-XXXX"
              value={form.inviteCode}
              onChange={e => setForm(p => ({ ...p, inviteCode: e.target.value.toUpperCase() }))}
              required={inviteRequired}
            />
          </div>
        )}

        {/* ToS elfogadás */}
        <label
          htmlFor="tosAccepted"
          className={`flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer
                      transition-colors duration-200 ${
            tosAccepted
              ? 'bg-brand-500/[0.07] border-brand-400/25'
              : 'bg-white/[0.02] border-white/[0.07] hover:border-white/[0.14]'
          }`}
        >
          <input
            type="checkbox"
            id="tosAccepted"
            checked={tosAccepted}
            onChange={e => setTosAccepted(e.target.checked)}
            className="checkbox mt-0.5"
          />
          <span className="text-xs text-gray-500 leading-relaxed">
            I have read and agree to the{' '}
            <Link to="/terms" target="_blank" className="link underline">Terms of Service</Link>
            {', '}
            <Link to="/rules" target="_blank" className="link underline">Rules &amp; Acceptable Use Policy</Link>
            {' and '}
            <Link to="/privacy" target="_blank" className="link underline">Privacy Policy</Link>
            , including the no-refund policy, automated/manual ban enforcement terms, and the
            collection of hardware and security data described in the Privacy Policy.
          </span>
        </label>

        {/* A gomb csak a ToS-en és a folyamatban lévő kérésen bukik el — a jelszó
            szabályait a validate() üzenete magyarázza el, hogy ne néma tiltás legyen. */}
        <button
          type="submit"
          className="btn-primary w-full py-3"
          disabled={loading || !tosAccepted}
        >
          {loading ? 'Creating account…' : 'Create Account'}
        </button>
      </form>
    </AuthShell>
  );
}
